import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/header';
import Footer from '../components/footer';
import { useSubscription } from '../contexts/SubscriptionContext';
import { waitForSubscriptionUpdate } from '../utils/subscriptionPolling';
import getApiBase from '../components/getApiBase';
import VoicePlanModal from '../components/VoicePlanModal';
import {
  VOICE_BASE_PLAN,
  cascadeVoiceSelection,
  filterCheckoutPlanIds,
  isVoicePlanName,
  voiceSubtotalFromSelection,
} from '../utils/voiceSkuCatalog';
import { dueMonthly, formatDueMonthly } from '../utils/retailCompDisplay';
import { confirmRemoveSubscriptionItem } from '../utils/subscriptionRemoveConfirm';

const Pricing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addPendingSubscription, updateSubscriptions } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [user, setUser] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Available plans from database
  const [bidTiers, setBidTiers] = useState([]);
  const [addons, setAddons] = useState([]);
  
  // Current selections
  const [selectedBidTier, setSelectedBidTier] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  
  // Current active subscriptions
  const [currentSubscriptions, setCurrentSubscriptions] = useState([]);
  const [prorationDialogOpen, setProrationDialogOpen] = useState(false);
  const [prorationData, setProrationData] = useState(null);
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [cancellingVoiceBase, setCancellingVoiceBase] = useState(false);
  const [retailComp, setRetailComp] = useState(false);

  useEffect(() => {
    const boot = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login?redirect=/pricing&role=Mechanic');
        return;
      }
      await fetchUserData();

      const status = searchParams.get('subscription');
      if (status === 'success') {
        console.log('Subscription successful!');
      } else if (status === 'cancelled') {
        console.log('Subscription cancelled');
      }
    };
    boot();
  }, [searchParams]);

  const fetchPlans = async (mechanicId = null, shopId = null) => {
    try {
      const apiBase = getApiBase();
      const token = localStorage.getItem('token');
      // Include mechanic_id and shop_id in query to get inactive plans user has subscriptions to
      let url = `${apiBase}/api/stripe/plans`;
      if (mechanicId && shopId) {
        url += `?mechanic_id=${mechanicId}&shop_id=${shopId}`;
      }
      
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: 'include',
      });
      const data = await response.json();
      
      if (response.ok) {
        const plans = data.plans || [];
        setRetailComp(Boolean(data.retailComp));
        
        // Separate bid tiers and add-ons
        const tiers = plans.filter(p => p.plan_type === 'bid_tier').sort((a, b) => a.display_order - b.display_order);
        const features = plans.filter(p => p.plan_type === 'addon').sort((a, b) => a.display_order - b.display_order);
        
        setBidTiers(tiers);
        setAddons(features);
      } else {
        setError('Failed to load pricing plans');
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load pricing plans');
    }
  };

  const fetchUserData = async () => {
    try {
      const apiBase = getApiBase();
      const token = localStorage.getItem('token');
      if (!token) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      const mechanicResponse = await fetch(`${apiBase}/api/mechanicProfile/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!mechanicResponse.ok) {
        // Car Owners / non-mechanics cannot manage shop subscriptions
        setAccessDenied(true);
        setError('Only shop owners can manage subscriptions.');
        setLoading(false);
        return;
      }

      const mechanicData = await mechanicResponse.json();
      const payload = JSON.parse(atob(token.split('.')[1]));

      // Any Owner role (primary or co-owner) — Mechanics.role === Owner
      if (mechanicData.role !== 'Owner') {
        setAccessDenied(true);
        setError('Only shop owners can manage subscriptions. Ask an owner to update plans.');
        setUser({
          ...mechanicData,
          mechanic_id: payload.id || payload.user_id,
          mechanic_role: mechanicData.role,
          shop_id: mechanicData.shop_id,
        });
        setLoading(false);
        return;
      }

      const enrichedUser = {
        ...mechanicData,
        mechanic_id: payload.id || payload.user_id,
        mechanic_role: mechanicData.role,
        shop_id: mechanicData.shop_id,
      };

      setUser(enrichedUser);
      await fetchPlans(enrichedUser.mechanic_id, enrichedUser.shop_id);
      await fetchCurrentSubscriptions(enrichedUser.mechanic_id, enrichedUser.shop_id);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setAccessDenied(true);
      setError('Unable to verify shop owner access.');
      setLoading(false);
    }
  };

  const fetchCurrentSubscriptions = async (mechanicId, shopId) => {
    try {
      const apiBase = getApiBase();
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${apiBase}/api/mechanicProfile/subscription?mechanic_id=${mechanicId}&shop_id=${shopId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCurrentSubscriptions(data.subscriptions || []);
        
        // Pre-select current subscriptions from the array
        // Skip cancelled subscriptions - they shouldn't be pre-selected
        if (data.subscriptions && data.subscriptions.length > 0) {
          data.subscriptions.forEach(sub => {
            // Don't pre-select cancelled subscriptions
            if (sub.status === 'cancelled') {
              return;
            }
            
            const plan = sub.plan;
            if (!plan) return;
            
            // Pre-select bid tier (radio button - only one)
            if (plan.plan_type === 'bid_tier') {
              setSelectedBidTier(plan.id);
            }
            
            // Pre-check addons (checkboxes - can be multiple)
            if (plan.plan_type === 'addon') {
              setSelectedAddons(prev => {
                if (!prev.includes(plan.id)) {
                  return [...prev, plan.id];
                }
                return prev;
              });
            }
          });
        }
      }
    } catch (err) {
      console.error('Error fetching current subscriptions:', err);
    }
  };

  const handleBidTierChange = (event) => {
    const tierId = parseInt(event.target.value);
    const tier = bidTiers.find(t => t.id === tierId);
    const currentBidTier = currentSubscriptions.find(sub => sub.plan?.plan_type === 'bid_tier');
    
    // If trying to change from an inactive tier, show warning
    if (currentBidTier && currentBidTier.plan?.is_active === 0 && tierId !== currentBidTier.plan.id) {
      const confirmed = window.confirm(
        `⚠️ WARNING: "${currentBidTier.plan.display_name}" is no longer available for new subscriptions.\n\n` +
        'If you change your bid tier, you will NOT be able to return to this tier later.\n\n' +
        'Are you sure you want to change?'
      );
      
      if (!confirmed) {
        return; // User cancelled, don't change
      }
    }
    
    setSelectedBidTier(tierId);
  };

  const handleAddonToggle = (addonId) => {
    // Check if this is an inactive plan that the user currently has
    const addon = addons.find(a => a.id === addonId);
    const currentSubscription = currentSubscriptions.find(sub => sub.plan?.id === addonId);
    
    // If trying to uncheck an inactive plan that user has, show warning
    if (selectedAddons.includes(addonId) && addon?.is_active === 0 && currentSubscription) {
      const confirmed = window.confirm(
        `⚠️ WARNING: "${addon.display_name}" is no longer available for new subscriptions.\n\n` +
        'If you remove this subscription, you will NOT be able to re-add it later.\n\n' +
        'Are you sure you want to remove it?'
      );
      
      if (!confirmed) {
        return; // User cancelled, don't uncheck
      }
    }
    
    setSelectedAddons(prev => {
      let next;
      if (prev.includes(addonId)) {
        next = prev.filter(id => id !== addonId);
      } else {
        next = [...prev, addonId];
      }
      return cascadeVoiceSelection(addons, prev, next);
    });
  };

  const formatPeriodEnd = (dateString) => {
    if (!dateString) return 'the end of your billing period';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  /** Toggle OFF Voice Base while subscribed → same REMOVE confirm + API as Active Plans. */
  const handleCancelActiveVoiceBase = async () => {
    const voiceBase = addons.find((a) => a.name === VOICE_BASE_PLAN);
    const voiceSub = currentSubscriptions.find(
      (s) =>
        s.plan?.id === voiceBase?.id &&
        (s.status === 'active' || s.status === 'trialing') &&
        s.cancel_at_period_end !== 1,
    );
    if (!voiceSub?.id) return;

    const planName = voiceSub.plan?.display_name || 'AI Phone Assistant';
    const confirmed = confirmRemoveSubscriptionItem({
      planName,
      periodEndDate: formatPeriodEnd(voiceSub.current_period_end),
      isVoiceBase: true,
      planIsActive: voiceSub.plan?.is_active !== 0,
    });
    if (!confirmed) return;

    try {
      setCancellingVoiceBase(true);
      const apiBase = getApiBase();
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBase}/api/stripe/remove-subscription-item`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ subscriptionId: voiceSub.id }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        alert(data.error || 'Failed to cancel Voice Base');
        return;
      }
      alert(
        data.message ||
          'Voice Base cancellation requested. Voice extras cancel with Base; the Voice phone number is released.',
      );
      // Drop Voice SKUs from cart selection so the modal toggle reflects cancel
      const voiceIds = addons
        .filter((a) => isVoicePlanName(a.name))
        .map((a) => a.id)
        .filter(Boolean);
      setSelectedAddons((prev) => prev.filter((id) => !voiceIds.includes(id)));
      if (user?.mechanic_id && user?.shop_id) {
        await fetchCurrentSubscriptions(user.mechanic_id, user.shop_id);
      }
      setVoiceModalOpen(false);
    } catch (err) {
      console.error('Error cancelling Voice Base from modal:', err);
      alert('Failed to cancel Voice Base');
    } finally {
      setCancellingVoiceBase(false);
    }
  };

  const calculateTotal = () => {
    let total = 0;
    
    // Add bid tier price
    if (selectedBidTier) {
      const tier = bidTiers.find(t => t.id === selectedBidTier);
      if (tier) {
        total += parseFloat(tier.monthly_price);
      }
    }
    
    // Add addon prices
    selectedAddons.forEach(addonId => {
      const addon = addons.find(a => a.id === addonId);
      if (addon) {
        total += parseFloat(addon.monthly_price);
      }
    });
    
    return total.toFixed(2);
  };

  const getCartItems = () => {
    const items = [];
    
    // Add bid tier
    if (selectedBidTier) {
      const tier = bidTiers.find(t => t.id === selectedBidTier);
      if (tier) {
        items.push({ name: tier.display_name, price: dueMonthly(tier.monthly_price, retailComp) });
      }
    }
    
    // Add addons
    selectedAddons.forEach(addonId => {
      const addon = addons.find(a => a.id === addonId);
      if (addon) {
        items.push({ name: addon.display_name, price: dueMonthly(addon.monthly_price, retailComp) });
      }
    });
    
    return items;
  };

  // Helper: Check if selections have changed from current subscriptions
  const hasChanges = () => {
    const currentBidTier = currentSubscriptions.find(sub => sub.plan?.plan_type === 'bid_tier');
    const currentAddonIds = currentSubscriptions
      .filter(sub => sub.plan?.plan_type === 'addon')
      .map(sub => sub.plan.id);
    
    // Check if bid tier changed
    const bidTierChanged = currentBidTier ? currentBidTier.plan.id !== selectedBidTier : selectedBidTier !== null;
    
    // Check if addons changed
    const addonsChanged = 
      selectedAddons.length !== currentAddonIds.length ||
      selectedAddons.some(id => !currentAddonIds.includes(id));
    
    return bidTierChanged || addonsChanged;
  };

  // Helper: Get button text based on state
  const getButtonText = () => {
    if (processing) return 'Processing...';
    if (!user) return 'Login to Subscribe';
    if (currentSubscriptions.length === 0) return 'Subscribe Now';
    if (!hasChanges()) return 'No Changes';
    return 'Update Subscription';
  };

  // Helper: Get changes summary
  const getChangesSummary = () => {
    const currentBidTier = currentSubscriptions.find(sub => sub.plan?.plan_type === 'bid_tier');
    const currentAddonIds = currentSubscriptions
      .filter(sub => sub.plan?.plan_type === 'addon')
      .map(sub => sub.plan.id);
    
    const changes = [];
    
    // Bid tier changes
    if (currentBidTier && selectedBidTier && currentBidTier.plan.id !== selectedBidTier) {
      const newTier = bidTiers.find(t => t.id === selectedBidTier);
      changes.push(`Upgrade to ${newTier?.display_name}`);
    } else if (!currentBidTier && selectedBidTier) {
      const newTier = bidTiers.find(t => t.id === selectedBidTier);
      changes.push(`Add ${newTier?.display_name}`);
    }
    
    // New addons
    const newAddonIds = selectedAddons.filter(id => !currentAddonIds.includes(id));
    newAddonIds.forEach(id => {
      const addon = addons.find(a => a.id === id);
      if (addon) changes.push(`Add ${addon.display_name}`);
    });
    
    return changes;
  };

  const handleCheckout = async () => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login?redirect=/pricing');
      return;
    }

    if (user?.mechanic_role && user.mechanic_role !== 'Owner') {
      alert('Only the shop owner can manage subscriptions and AI Phone Assistant.');
      return;
    }

    // Check if anything is selected — empty OK when cancelling existing (e.g. Voice Base only)
    if (!selectedBidTier && selectedAddons.length === 0 && currentSubscriptions.length === 0) {
      alert('Please select at least one subscription option');
      return;
    }

    // Check if anything changed
    if (currentSubscriptions.length > 0 && !hasChanges()) {
      alert('No changes detected. Select a different bid tier or toggle addons to update your subscription.');
      return;
    }

    try {
      const apiBase = getApiBase();
      setProcessing(true);

      // Collect all selected plan IDs (Voice Text Chat never billed; Voice extras need Stripe price)
      const planIds = [];
      if (selectedBidTier) planIds.push(selectedBidTier);
      const { allowed: checkoutAddonIds, skipped } = filterCheckoutPlanIds(
        addons,
        selectedAddons,
      );
      planIds.push(...checkoutAddonIds);
      if (skipped.length > 0) {
        const msg = skipped
          .map((s) => s.reason)
          .join('\n');
        alert(
          `Some Voice options were not sent to checkout:\n\n${msg}\n\nVoice Base can still check out if selected. Calendar/Recording need Stripe prices; Text Chat is UI-only until EZ is ready.`,
        );
      }

      // Voice Base only (or all Voice deselected with nothing else to checkout): cancel via Stripe cancel API
      if (planIds.length === 0) {
        const voiceBase = addons.find((a) => a.name === VOICE_BASE_PLAN);
        const voiceSub = currentSubscriptions.find((s) => s.plan?.id === voiceBase?.id);
        if (voiceSub?.id && voiceBase && !selectedAddons.includes(voiceBase.id)) {
          const cancelRes = await fetch(`${apiBase}/api/stripe/cancel-subscription`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ subscriptionId: voiceSub.id }),
          });
          const cancelData = await cancelRes.json().catch(() => ({}));
          if (!cancelRes.ok) {
            alert(cancelData.error || 'Failed to cancel Voice Base');
            setProcessing(false);
            return;
          }
          alert(
            cancelData.message ||
              'Voice Base cancellation requested. Add-ons cancel with Base; the Voice phone number is released.',
          );
          await fetchCurrentSubscriptions(user.mechanic_id, user.shop_id);
          setProcessing(false);
          return;
        }
        alert('Please select at least one subscription option that can be checked out.');
        setProcessing(false);
        return;
      }

      const response = await fetch(`${apiBase}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          planIds, // Array of plan IDs
          billingCycle: 'monthly',
          mechanicId: user.mechanic_id,
          shopId: user.shop_id,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.syncedFromStripe) {
          updateSubscriptions(data.subscriptions || []);
          alert(data.message || 'Your subscription is already active. Your account has been updated.');
          navigate('/mechanic-profile?subscription=updated');
          setProcessing(false);
        } else if (data.addedToExisting) {
          // ✅ Show proration confirmation if prorated amounts are provided
          if (data.prorated_estimates && data.prorated_estimates.length > 0) {
            setProrationData(data);
            setProrationDialogOpen(true);
            // Items are already added to Stripe, so we'll wait for webhook after user confirms
          }
          
          // ✅ Items added to Stripe - now wait for webhook to complete
          console.log('✅ Items added to Stripe subscription, waiting for confirmation...');
          
          // Add to pending state (optimistic UI)
          addPendingSubscription(planIds);
          
          setProcessingMessage('Confirming subscription...');
          
          // Wait for webhook to complete (with polling + reconciliation fallback)
          const result = await waitForSubscriptionUpdate({
            mechanicId: user.mechanic_id,
            shopId: user.shop_id,
            expectedPlanIds: planIds,
            onProgress: (progress) => {
              if (progress.phase === 'polling') {
                setProcessingMessage('Confirming subscription...');
              } else if (progress.phase === 'reconciling') {
                setProcessingMessage('Syncing with Stripe...');
              } else if (progress.phase === 'complete') {
                setProcessingMessage('Success!');
              }
            }
          });
          
          if (result.success) {
            // Update context with confirmed subscriptions
            updateSubscriptions(result.subscriptions);
            
            // ✅ Fetch actual invoice to show real prorated charge
            if (data.prorated_estimates && data.prorated_estimates.length > 0) {
              try {
                const token = localStorage.getItem('token');
                const historyResponse = await fetch(
                  `${apiBase}/api/mechanicProfile/subscription-history?mechanic_id=${user.mechanic_id}&shop_id=${user.shop_id}`,
                  {
                    headers: { Authorization: `Bearer ${token}` },
                    credentials: 'include',
                  }
                );
                
                if (historyResponse.ok) {
                  const historyData = await historyResponse.json();
                  // Find the most recent charge with prorated items
                  const recentCharge = historyData.transactions?.find((t: any) => 
                    t.type === 'charge' && 
                    t.line_items?.some((item: any) => item.is_proration)
                  );
                  
                  if (recentCharge) {
                    const proratedItems = recentCharge.line_items.filter((item: any) => item.is_proration);
                    const totalProrated = proratedItems.reduce((sum: number, item: any) => sum + parseFloat(item.amount || 0), 0);
                    
                    alert(
                      `✅ Subscriptions updated successfully!\n\n` +
                      `💰 You were charged $${totalProrated.toFixed(2)} for prorated subscription(s).\n` +
                      `📋 View your billing history to see the detailed breakdown.`
                    );
                  } else {
                    alert(
                      `✅ Subscriptions updated successfully!\n\n` +
                      `💰 A prorated charge has been processed. Check your billing history for details.`
                    );
                  }
                } else {
                  alert(data.message || 'Subscriptions updated successfully!');
                }
              } catch (err) {
                console.error('Error fetching invoice:', err);
                alert(
                  `✅ Subscriptions updated successfully!\n\n` +
                  `💰 A prorated charge has been processed. Check your billing history for details.`
                );
              }
            } else {
              alert(data.message || 'Subscriptions updated successfully!');
            }
            
            navigate('/mechanic-profile?subscription=updated');
          } else {
            // Polling and reconciliation both failed
            console.error('❌ Subscription update failed:', result.error);
            alert(
              'Subscription update is processing. ' +
              'Please refresh the page in a moment to see your updated subscriptions. ' +
              'If the issue persists, contact support.'
            );
            setProcessing(false);
          }
        } else if (data.url) {
          // New subscription - redirect to Stripe Checkout
          window.location.href = data.url;
        } else {
          alert('Unexpected response from server');
          setProcessing(false);
        }
      } else {
        alert(data.error || 'Failed to create checkout session');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      alert('Failed to start checkout process');
      setProcessing(false);
    }
  };

  // Group addons by category
  const visibilityAddons = addons.filter(a => 
    ['addon_priority_listing', 'addon_featured_badge', 'addon_google_reviews'].includes(a.name)
  );
  
  const managementAddons = addons.filter(a => 
    ['addon_service_history', 'addon_analytics', 'addon_branding'].includes(a.name)
  );

  // TASK-589 / TASK-769 — Voice: shop Owners only
  const isShopOwner = user?.mechanic_role === "Owner";
  const voiceAddons = isShopOwner
    ? addons.filter((a) => isVoicePlanName(a.name))
    : [];
  const voiceBasePlan = voiceAddons.find((a) => a.name === VOICE_BASE_PLAN);
  const activeVoiceBaseSubscription = currentSubscriptions.find(
    (s) =>
      s.plan?.name === VOICE_BASE_PLAN &&
      (s.status === 'active' || s.status === 'trialing'),
  );
  const voiceSubtotal = voiceSubtotalFromSelection(addons, selectedAddons);
  const otherAddonSubtotal = selectedAddons.reduce((sum, id) => {
    const a = addons.find((x) => x.id === id);
    if (!a || isVoicePlanName(a.name)) return sum;
    return sum + (parseFloat(a.monthly_price) || 0);
  }, 0);
  const bidSubtotal = selectedBidTier
    ? parseFloat(bidTiers.find((t) => t.id === selectedBidTier)?.monthly_price || 0)
    : 0;

  if (loading) {
    return (
      <>
        <Header />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress />
        </Box>
        <Footer />
      </>
    );
  }

  if (accessDenied || !isShopOwner) {
    return (
      <>
        <Header />
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            {error ||
              "Only shop owners can manage subscriptions, monthly statements, and plan changes. Mechanics and car owners do not have access."}
          </Alert>
          <Button variant="contained" onClick={() => navigate("/mechanic-profile")}>
            Back to Profile
          </Button>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Manage Your Subscription
        </Typography>
        <Paper elevation={2} sx={{ px: 3, py: 1, bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h6" fontWeight="bold">
            Cart: ${calculateTotal()}/mo
          </Typography>
        </Paper>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Bid Frequency Section - Now controlled by is_active in database */}
      {bidTiers.length > 0 && (
      <Card elevation={3} sx={{ mb: 3, p: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          SUBSCRIPTION PLANS (Choose One)
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <RadioGroup value={selectedBidTier || ''} onChange={handleBidTierChange}>
          {bidTiers.map((tier) => {
            const features = tier.features && Array.isArray(tier.features) ? tier.features : [];
            return (
              <Box
                key={tier.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 1.5,
                  px: 2,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <FormControlLabel
                  value={tier.id}
                  control={<Radio />}
                  label={
                    <Box width="100%">
                      <Box display="flex" alignItems="center" width="100%">
                        <Typography variant="body1" fontWeight="bold" color="primary" sx={{ minWidth: 180 }}>
                          {tier.display_name} - ${formatDueMonthly(tier.monthly_price, retailComp)}/mo
                        </Typography>
                        {tier.is_active === 0 && (
                          <Chip
                            label="NO LONGER AVAILABLE"
                            color="warning"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                          {features.length > 0 ? `• ${features.join(' • ')}` : tier.description}
                        </Typography>
                      </Box>
                      {tier.is_active === 0 && currentSubscriptions.find(sub => sub.plan?.id === tier.id) && (
                        <Typography variant="body2" color="warning.main" sx={{ mt: 0.5, ml: 4.5, fontWeight: 'bold' }}>
                          ⚠️ This plan is no longer available. If changed, it cannot be re-selected.
                        </Typography>
                      )}
                      {(() => {
                        const currentSub = currentSubscriptions.find(sub => sub.plan?.id === tier.id);
                        if (currentSub?.cancel_at_period_end === 1 && currentSub?.current_period_end) {
                          const cancelDate = new Date(currentSub.current_period_end).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          });
                          return (
                            <Typography variant="body2" color="warning.main" sx={{ mt: 0.5, ml: 4.5, fontWeight: 'bold' }}>
                              ⚠️ Cancels on {cancelDate}
                            </Typography>
                          );
                        }
                        return null;
                      })()}
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </Box>
            );
          })}
        </RadioGroup>
      </Card>
      )}

      {/* TASK-769 / TASK-806 — AI Phone Assistant only when catalog includes Base */}
      {isShopOwner && voiceBasePlan && (
        <Card elevation={3} sx={{ mb: 3, p: 3, borderLeft: "4px solid", borderColor: "primary.main" }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            AI PHONE ASSISTANT
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Optional Voice plans — same subscription page as your other add-ons.
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Typography variant="body1" fontWeight="medium">
              Voice subtotal: ${voiceSubtotal.toFixed(2)}/mo
            </Typography>
            <Button
              variant="contained"
              onClick={() => setVoiceModalOpen(true)}
              disabled={!voiceBasePlan && voiceAddons.length === 0}
            >
              Configure Voice plan
            </Button>
          </Box>
        </Card>
      )}

      {isShopOwner && voiceBasePlan && (
        <VoicePlanModal
          open={voiceModalOpen}
          onClose={() => setVoiceModalOpen(false)}
          addons={addons}
          selectedAddonIds={selectedAddons}
          setSelectedAddonIds={setSelectedAddons}
          activeVoiceBaseSubscription={activeVoiceBaseSubscription}
          onCancelActiveBase={handleCancelActiveVoiceBase}
          cancellingBase={cancellingVoiceBase}
          retailComp={retailComp}
        />
      )}

      {/* Visibility & Marketing Section */}
      {visibilityAddons.length > 0 && (
        <Card elevation={3} sx={{ mb: 3, p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            VISIBILITY & MARKETING
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {visibilityAddons.map((addon) => {
            const features = addon.features && Array.isArray(addon.features) ? addon.features : [];
            return (
              <Box
                key={addon.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 1.5,
                  px: 2,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedAddons.includes(addon.id)}
                      onChange={() => handleAddonToggle(addon.id)}
                    />
                  }
                  label={
                    <Box width="100%">
                      <Box display="flex" alignItems="center" width="100%">
                        <Typography variant="body1" fontWeight="bold" color="primary" sx={{ minWidth: 220 }}>
                          {addon.display_name} - ${formatDueMonthly(addon.monthly_price, retailComp)}/mo
                        </Typography>
                        {addon.is_active === 0 && (
                          <Chip
                            label="NO LONGER AVAILABLE"
                            color="warning"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                          {features.length > 0 ? `• ${features.join(' • ')}` : addon.description}
                        </Typography>
                      </Box>
                      {addon.is_active === 0 && currentSubscriptions.find(sub => sub.plan?.id === addon.id) && (
                        <Typography variant="body2" color="warning.main" sx={{ mt: 0.5, ml: 4.5, fontWeight: 'bold' }}>
                          ⚠️ This plan is no longer available. If removed, it cannot be re-added.
                        </Typography>
                      )}
                      {(() => {
                        const currentSub = currentSubscriptions.find(sub => sub.plan?.id === addon.id);
                        if (currentSub?.cancel_at_period_end === 1 && currentSub?.current_period_end) {
                          const cancelDate = new Date(currentSub.current_period_end).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          });
                          return (
                            <Typography variant="body2" color="warning.main" sx={{ mt: 0.5, ml: 4.5, fontWeight: 'bold' }}>
                              ⚠️ Cancels on {cancelDate}
                            </Typography>
                          );
                        }
                        return null;
                      })()}
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </Box>
            );
          })}
        </Card>
      )}

      {/* Shop Management Section */}
      {managementAddons.length > 0 && (
        <Card elevation={3} sx={{ mb: 3, p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            SHOP MANAGEMENT
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {managementAddons.map((addon) => {
            const features = addon.features && Array.isArray(addon.features) ? addon.features : [];
            return (
              <Box
                key={addon.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 1.5,
                  px: 2,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedAddons.includes(addon.id)}
                      onChange={() => handleAddonToggle(addon.id)}
                    />
                  }
                  label={
                    <Box width="100%">
                      <Box display="flex" alignItems="center" width="100%">
                        <Typography variant="body1" fontWeight="bold" color="primary" sx={{ minWidth: 220 }}>
                          {addon.display_name} - ${formatDueMonthly(addon.monthly_price, retailComp)}/mo
                        </Typography>
                        {addon.is_active === 0 && (
                          <Chip
                            label="NO LONGER AVAILABLE"
                            color="warning"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                          {features.length > 0 ? `• ${features.join(' • ')}` : addon.description}
                        </Typography>
                      </Box>
                      {addon.is_active === 0 && currentSubscriptions.find(sub => sub.plan?.id === addon.id) && (
                        <Typography variant="body2" color="warning.main" sx={{ mt: 0.5, ml: 4.5, fontWeight: 'bold' }}>
                          ⚠️ This plan is no longer available. If removed, it cannot be re-added.
                        </Typography>
                      )}
                      {(() => {
                        const currentSub = currentSubscriptions.find(sub => sub.plan?.id === addon.id);
                        if (currentSub?.cancel_at_period_end === 1 && currentSub?.current_period_end) {
                          const cancelDate = new Date(currentSub.current_period_end).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          });
                          return (
                            <Typography variant="body2" color="warning.main" sx={{ mt: 0.5, ml: 4.5, fontWeight: 'bold' }}>
                              ⚠️ Cancels on {cancelDate}
                            </Typography>
                          );
                        }
                        return null;
                      })()}
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </Box>
            );
          })}
        </Card>
      )}

      {/* Cart Summary */}
      <Card elevation={4} sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          YOUR SUBSCRIPTION
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {getCartItems().length > 0 ? (
          <>
            {getCartItems().map((item, index) => (
              <Box key={index} display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1">• {item.name}</Typography>
                <Typography variant="body1" fontWeight="medium">
                  ${item.price.toFixed(2)}
                </Typography>
              </Box>
            ))}
            <Divider sx={{ my: 2 }} />
            {(voiceSubtotal > 0 || bidSubtotal > 0 || otherAddonSubtotal > 0) && (
              <Box sx={{ mb: 2 }}>
                {voiceSubtotal > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">Voice subtotal</Typography>
                    <Typography variant="body2">${voiceSubtotal.toFixed(2)}</Typography>
                  </Box>
                )}
                {bidSubtotal > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">Bid tier</Typography>
                    <Typography variant="body2">${bidSubtotal.toFixed(2)}</Typography>
                  </Box>
                )}
                {otherAddonSubtotal > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">Other add-ons</Typography>
                    <Typography variant="body2">${otherAddonSubtotal.toFixed(2)}</Typography>
                  </Box>
                )}
              </Box>
            )}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight="bold">
                Grand total (monthly): ${calculateTotal()}
              </Typography>
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => navigate('/mechanic-profile')}
                  sx={{ minWidth: 120 }}
                >
                  ← Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleCheckout}
                  disabled={processing || (currentSubscriptions.length > 0 && !hasChanges())}
                  sx={{ minWidth: 200 }}
                >
                  {processing ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                      <span>{processingMessage || 'Processing...'}</span>
                    </Box>
                  ) : (
                    `${getButtonText()} →`
                  )}
                </Button>
              </Box>
            </Box>
          </>
        ) : (
          <Typography variant="body1" color="text.secondary">
            No items selected. Choose a bid frequency or add-ons above.
          </Typography>
        )}
      </Card>
    </Container>
    <Footer />
    </>
  );
};

export default Pricing;
